import { removeIfStartsWith, suffixIfDoesNotExist } from './string';
import { getUri } from './uri';

/**
 * @see JRCImgProps
 */
type WidthHeight = {
    width?: number;
    height?: number;
};

/**
 * @see JRCImgProps
 */
type Size = {
    size?: string;
};

/**
 * @see JRCImgProps
 */
type Format = { format?: string };

/**
 * @see JRCImgProps
 */
type Timestamp = { timestamp?: string | number | undefined };

type Args = Timestamp & { iconColor?: 'white' };

/**
 * @see JRCImgProps
 */
export type ImgUrlProps = Timestamp & {
    url?: string;
    fallbackUrl?: string;

    size?: string;
    width?: number;
    height?: number;
    maxWidth?: string;

    from?: string;
    uri?: string;
    recordId?: string | number;
    type?: string;
    format?: string;

    iconColor?: 'white';
};

export function getFrom(arg: Pick<ImgUrlProps, 'from'>) {
    return removeIfStartsWith(suffixIfDoesNotExist(arg.from, '/'), '/') || '';
}

export function getTimestamp(arg: Timestamp) {
    return arg.timestamp ? `?_=${arg.timestamp}` : '';
}

export function getArgs(arg: Args) {
    const params = [];
    if ('timestamp' in arg && arg.timestamp) {
        params.push(`_=${arg.timestamp}`);
    }
    if ('iconColor' in arg && arg.iconColor) {
        params.push(`color=${arg.iconColor}`);
    }
    return params.length ? '?' + params.join('&') : '';
}

export function getSize(arg: Size & WidthHeight, dpr = 2): string {
    if ('size' in arg && arg.size) return arg.size;
    else if ('width' in arg && arg.width && 'height' in arg && arg.height)
        return `${arg.width * dpr}x${arg.height * dpr}`;
    return '';
}

export function getFormat(arg: Format) {
    return arg.format || 'png';
}

export function getUrl(url: string): URL | undefined {
    try {
        return new URL(url);
    } catch (e) {
        /* silent exception */
    }

    try {
        return new URL(window.location.origin + '/' + removeIfStartsWith(url, '/'));
    } catch (e) {
        /* silent exception */
    }

    return;
}

export function formatImgUrl(args: ImgUrlProps, dpr = 2) {
    // If URL is provided, use it directly with optional timestamp
    if ('url' in args && args.url) {
        const url = getUrl(args.url);
        if (url) {
            if ('timestamp' in args && args.timestamp && !url.searchParams.has('_')) {
                url.searchParams.append('_', args.timestamp.toString());
            }
            return url.toString();
        }
    }

    // If no url is provided, try to construct a valid url from the uri, type, recordId, and format
    try {
        const uri =
            args.uri?.indexOf('icons/') === 0
                ? args.uri
                : args.type === 'icons'
                ? args.type + '/' + args.recordId
                : getUri(args);
        return `/${getFrom(args)}${getSize(args, dpr)}/${uri}.${getFormat(args)}${getArgs(args)}`;
    } catch (error) {
        // Return an empty string if the uri is broken
        return '';
    }
}
